<template>
    <div class="appointment-calendar">
        <b-row class="action-row">
            <b-btn class="mr-3" @click="emrSync()" v-if="(emrReadEnabled || emrWriteEnabled) && practitionerView">
                {{ $i18n.gt('LABEL_APPOINTMENT_SYNC') }}
            </b-btn>
            <b-btn class="mr-3" @click="showCloneModal" v-if="practitionerView">{{
                    $i18n.gt('LABEL_APPOINTMENT_CLONE')
                }}
            </b-btn>
            <b-btn variant="danger" @click="showClearModal" v-if="practitionerView">
                {{ $i18n.gt('LABEL_APPOINTMENT_CLEAR') }}
            </b-btn>
        </b-row>
        <b-row class="zoom-container">
            <b-btn @click="zoomIn">
                <b-icon icon="zoom-in"/>
            </b-btn>
            <b-btn @click="zoomOut">
                <b-icon icon="zoom-out"/>
            </b-btn>
        </b-row>
        <b-row class="detail-container">
            <appointment-details class="details-col"
                                 :class="$mq === 'sm' ? 'col' : 'col-3'"
                                 :practitioner-view="practitionerView"
                                 :loading-details="loadingDetails"
                                 :current-appointment="currentAppointment"
                                 :edit-appointment="editAppointment"
                                 :cancel-appointment="cancelAppointment"
                                 :delete-appointment="deleteAppointment"
                                 :claim-appointment="claimAppointment"/>
            <div :class="$mq === 'sm' ? 'col' : 'col-9'">
                <FullCalendar :options="calendarOptions" ref="fullCal"/>
            </div>
        </b-row>
        <!-- Modals -->
        <div>
            <appointment-edit-modal
                :practitioner-view="practitionerView"
                :reload-calendar="reloadCalendar"
                :reload-selected="reloadSelectedAppointment"
                ref="apptEditModal"/>
            <appointment-clone-modal
                ref="apptCloneModal"
                :reload-calendar="reloadCalendar"
                :reload-selected="reloadSelectedAppointment"/>
            <appointment-clear-modal
                ref="apptClearModal"
                :reload-calendar="reloadCalendar"
                :reload-selected="reloadSelectedAppointment"/>
            <appointment-date-jump-modal
                ref="apptDateJumpModal"
                :reload-calendar="reloadCalendar"
                :reload-selected="reloadSelectedAppointment"
                @jump="jumpToDate"/>
        </div>
    </div>
</template>

<script>

import Vue from 'vue'
import * as _ from 'lodash'
import '@fullcalendar/core/vdom'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import esLocale from '@fullcalendar/core/locales/es'
import Utils from '@/providers/Utils'
import Emr from '@/providers/Emr'

export default Vue.extend({
    name: 'AppointmentCalendar',
    components: {
        FullCalendar
    },
    props: {
        practitionerView: Boolean
    },
    data() {
        return {
            slotDurationOptions: ['00:15:00', '00:30:00', '01:00:00', '02:00:00'],
            scrollOnZoom: ['09:00', '09:00', '06:00', '00:00'],
            currentAppointment: null,
            loadingDetails: false,
            calendarOptions: {
                plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
                locales: [esLocale],
                locale: this.$store.state.language,
                initialView: 'timeGridWeek',
                headerToolbar: {
                    start: 'prev,today,next',
                    center: 'title',
                    end: 'timeGridDay,timeGridThreeDay,timeGridWeek,dayGridMonth'
                },
                views: {
                    timeGridThreeDay: {
                        type: 'timeGrid',
                        dayCount: 3,
                        buttonText: '3 day',
                    }
                },
                slotDuration: '00:30:00',
                events: this.getAppointments,
                eventClick: this.onEventClick,
                height: '90vh',
                expandRows: true,
                scrollTime: '09:00:00',
                selectable: this.practitionerView,
                select: this.selectNewEvent
            },
            emrReadEnabled: false,
            emrWriteEnabled: false
        }
    },
    watch: {
        '$store.state.language': {
            handler: function (newVal, oldVal) {
                const calApi = this.$refs.fullCal.getApi()
                calApi.setOption('locale', newVal)
                calApi.refetchEvents()
            }
        },
    },
    mounted() {
        if (this.$mq === 'sm') this.loadMobile()
        this.$api.emrScope(this, {
            user: this.$store.state.currentUser.id
        })
            .then((scope) => {
                if (scope) {
                    this.emrReadEnabled = scope.includes('appointment.read')
                    this.emrWriteEnabled = scope.includes('appointment.write')
                }
            })
    },
    methods: {
        formatTimeDetails: Utils.formatTimeDetails,
        formatDayDetails: Utils.formatDayDetails,
        async getAppointments(info) {
            const appointmentsArray = await this.$api.getCalendarAppointments(this.$store.state.currentUser.id, Utils.mysqlDatetime(info.start), Utils.mysqlDatetime(info.end))
            let appointments = []
            _.each(appointmentsArray, (val) => {
                let titleName
                let eventColor
                if (this.practitionerView) {
                    titleName = val.participantName ? val.participantName : this.$i18n.gt('LABEL_APPOINTMENT_AVAILABLE')
                    eventColor = val.participantName ? null : '#20c997'
                } else {
                    titleName = val.participantName ? val.practitionerName : this.$i18n.gt('LABEL_APPOINTMENT_AVAILABLE') + ': ' + val.practitionerName
                    eventColor = val.participantName ? null : '#20c997'
                }
                appointments.push({id: val.id, start: val.start, end: val.end, title: titleName, color: eventColor})
            })
            return appointments
        },
        async onEventClick(info) {
            this.loadingDetails = true
            this.currentAppointment = null
            this.currentAppointment = await this.$api.getItem(this, 'appointment', info.event.id, ['participant'])
            this.loadingDetails = false
        },
        selectNewEvent(info) {
            if (this.practitionerView) {
                this.$refs.apptEditModal.id = null;
                this.$refs.apptEditModal.start = info.start
                this.$refs.apptEditModal.end = info.end
                this.$refs.apptEditModal.setAppointmentTime()
                Vue.nextTick(() => {
                    this.$refs.apptEditModal.showModal()
                })
            }
        },
        editAppointment() {
            if (this.practitionerView) {
                this.$refs.apptEditModal.start = this.currentAppointment.start
                this.$refs.apptEditModal.end = this.currentAppointment.end
                this.$refs.apptEditModal.id = this.currentAppointment.id
                this.$refs.apptEditModal.appointmentInstance.participant_id = this.currentAppointment.participant
                this.$refs.apptEditModal.appointmentInstance.emr_id = this.currentAppointment.emr_id
                this.$refs.apptEditModal.setAppointmentTime()
                Vue.nextTick(() => {
                    this.$refs.apptEditModal.showModal()
                })
            }
        },
        async cancelAppointment() {
            if (await this.$bvModal.msgBoxConfirm('Cancel appointment?', {
                okTitle: this.$i18n.gt('LABEL_APPOINTMENT_CANCEL'),
                okVariant: 'danger',
                cancelTitle: this.$i18n.gt('LABEL_APPOINTMENT_KEEP'),
                cancelVariant: 'primary'
            })) {
                let tempAppointment = this.currentAppointment
                tempAppointment.participant_id = null
                await this.$api.updateItem(this, 'appointment', tempAppointment, false)
                await this.reloadSelectedAppointment(this.currentAppointment.id)
                this.reloadCalendar()
            }
        },
        async claimAppointment() {
            if (await this.$bvModal.msgBoxConfirm('Claim appointment?', {
                okTitle: this.$i18n.gt('LABEL_APPOINTMENT_CLAIM'),
                cancelTitle: this.$i18n.gt('LABEL_CANCEL'),
                cancelVariant: 'primary'
            })) {
                let tempAppointment = this.currentAppointment
                tempAppointment.participant_id = this.$user.participantId()
                await this.$api.updateItem(this, 'appointment', tempAppointment, false)
                await this.reloadSelectedAppointment(this.currentAppointment.id)
                this.reloadCalendar()
            }
        },
        async deleteAppointment() {
            if (await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this appointment? This cannot be undone.', {okVariant: 'danger', okTitle: this.$i18n.gt('LABEL_DELETE'), cancelTitle: this.$i18n.gt('LABEL_CANCEL')})) {
                try {
                    if (this.currentAppointment.emr_id) {
                        let result = await this.$api.emrDelete(this, 'appointment', this.currentAppointment.emr_id, this.$user.practitionerPracticeId())
                        if (result) {
                            //  TODO: check for errors
                        }
                    }
                    let result = await this.$api.deleteItem(this, 'appointment', this.currentAppointment)
                    this.currentAppointment = null
                    this.reloadCalendar()
                } catch (e) {
                    this.$utils.handleError(e, 'Could not delete appointment')
                }
            }
        },
        reloadCalendar() {
            this.$refs.fullCal.getApi().refetchEvents()
        },
        async reloadSelectedAppointment(id) {
            this.loadingDetails = true
            this.currentAppointment = null
            if (id) {
                this.currentAppointment = await this.$api.getItem(this, 'appointment', id, ['participant'])
            }
            this.loadingDetails = false
        },
        showCloneModal() {
            this.$refs.apptCloneModal.showModal()
        },
        showClearModal() {
            this.$refs.apptClearModal.showModal()
        },
        showDateJumpModal() {
            this.$refs.apptDateJumpModal.showModal()
        },
        loadMobile() {
            this.showDateJumpModal()
            this.$refs.fullCal.getApi().changeView('timeGridDay')
        },
        jumpToDate(date) {
            this.$refs.fullCal.getApi().gotoDate(date)
        },
        zoomIn() {
            const cal = this.$refs.fullCal.getApi()
            const current = cal.getOption('slotDuration')
            const index = this.slotDurationOptions.indexOf(current)
            if (index !== 0) {
                cal.setOption('slotDuration', this.slotDurationOptions[index - 1])
            }
            cal.scrollToTime(this.scrollOnZoom[index - 1])
        },
        zoomOut() {
            const cal = this.$refs.fullCal.getApi()
            const current = cal.getOption('slotDuration')
            const index = this.slotDurationOptions.indexOf(current)
            if (index !== this.slotDurationOptions.length - 1) {
                cal.setOption('slotDuration', this.slotDurationOptions[index + 1])
            }
            cal.scrollToTime(this.scrollOnZoom[index + 1])
        },
        emrSync() {
            Emr.syncCalendar(this.$store.state.currentUser.id)
                .then(result => {
                    if (result) {
                        this.$toasted.show('Appointments synchronized', {duration: 3000})
                        this.reloadCalendar()
                    }
                })
        }
    },
})
</script>

<style lang="scss">

@import '../../sass/variables_mixins';

.appointment-calendar {
    .detail-container {
        @media (max-width: 576px) {
            flex-direction: column-reverse;
        }
    }

    .zoom-container {
        justify-content: end;
        padding: 0 15px 15px 0;

        @media(max-width: 567px) {
            justify-content: center;
            padding: 30px 0 15px 0;
        }

        button {
            background-color: var(--green);

            &:last-child {
                border-radius: 0 3px 3px 0;
            }

            &:first-child {
                border-radius: 3px 0 0 3px;
            }

            &:hover, &:active, &:focus {
                background-color: var(--green) !important;
                filter: brightness(110%);
                border-color: var(--green) !important;
            }
        }
    }

    .fc-header-toolbar {
        @media (max-width: 576px) {
            margin-top: 1em;
            flex-direction: column-reverse;
            .fc-toolbar-chunk {
                margin: .25em 0;
            }
        }
    }

    .fc {
        .fc-col-header-cell-cushion {
            color: var(--base-color);
        }

        .fc-daygrid-event, .fc-daygrid-day-number {
            color: #000;
        }

        .fc-timegrid-slot {
            height: 2.5em;
        }

        .fc-event-main {
            cursor: pointer;
        }

        a, .fc-daygrid-event {
            cursor: pointer;
        }
    }

    .details-col {
        margin-top: 4em;

        .appointment-details .detail-header {
            margin-bottom: 1em;
        }

        .detail-actions {
            margin-top: 0;
            padding: 0 1em;

            .btn {
                margin-right: 1em;
            }
        }
    }

    .action-row {
        margin: -3em 0 1em 0;
        justify-content: end;
        @media (max-width: 576px) {
            margin: 0;
            justify-content: space-evenly;
            button.btn {
                width: 40%;
            }
        }
    }

    table > tbody > tr > th, table > tbody > tr > td {
        padding: 0 !important;
    }

    table th, table td {
        vertical-align: unset !important;
    }
}

</style>
